import React, { useEffect, useReducer } from "react";
import { Cell } from "@components/table/types";
import { M_TABLE_HEADERS_LANDING_ZONE_ROLES } from "../constants";
import { LandingZoneRoleModel } from "@api/types/landingzoneroles";
import { getLandingZoneRoles } from "@api/admin/landingzoneroles";
import { alertService } from "@services";
import { FilterType, Form, Table } from "@components";
import { Box } from "@material-ui/core";
import { MaintenanceBreadcrumbs } from "../breadcrumbs";
import { LandingZoneRoleDetail } from "./detail";
import { useLandingZoneRoleStore } from "../../../../../store/hooksy/landingZoneRoles";

const headers: Cell[] = [
  { data: M_TABLE_HEADERS_LANDING_ZONE_ROLES.NAME },
  { data: M_TABLE_HEADERS_LANDING_ZONE_ROLES.IS_ACTIVE },
];

type State = {
  landingZoneRoles: LandingZoneRoleModel[];
  loading: boolean;
  selectedLandingZoneRoleId: number;
  openModal: boolean;
};

type Action =
  | {
      type: "fetch-success";
      value: State["landingZoneRoles"];
      flag: State["loading"];
    }
  | {
      type: "toggle-loading";
      value: State["loading"];
    }
  | {
      type: "toggle-modal";
      value: State["openModal"];
      selectedId?: State["selectedLandingZoneRoleId"];
    };

function init(): State {
  return {
    landingZoneRoles: [],
    loading: false,
    selectedLandingZoneRoleId: 0,
    openModal: false,
  };
}

function reducer(state: State, action: Action) {
  switch (action.type) {
    case "fetch-success":
      return {
        ...state,
        landingZoneRoles: action.value,
        loading: action.flag,
      };
    case "toggle-loading":
      return {
        ...state,
        loading: action.value === undefined ? !state.loading : action.value,
      };
    case "toggle-modal":
      return {
        ...state,
        openModal: action.value === undefined ? !state.openModal : action.value,
        selectedLandingZoneRoleId: action.selectedId ? action.selectedId : 0,
      };
    default:
      return state;
  }
}

export const LandingZoneRoles: React.FC = () => {
  const [localState, localDispatch] = useReducer(reducer, undefined, init);
  const [
    landingZoneRoleHooksyStore,
    setLandingZoneRoleHooksyStore,
  ] = useLandingZoneRoleStore();

  function fetchLandingZoneRoles() {
    localDispatch({ type: "toggle-loading", value: true });
    getLandingZoneRoles()
      .then((response) =>
        localDispatch({ type: "fetch-success", value: response, flag: false })
      )
      .catch((error) => {
        alertService.danger({ statusCode: error.response.status });
        localDispatch({ type: "toggle-loading", value: false });
      })
      .finally(() => setLandingZoneRoleHooksyStore({ fetchList: false }));
  }

  useEffect(() => {
    fetchLandingZoneRoles();
  }, []);

  useEffect(() => {
    if (landingZoneRoleHooksyStore.fetchList) {
      fetchLandingZoneRoles();
    }
  }, [landingZoneRoleHooksyStore]);

  useEffect(() => {
    if (localState.openModal && localState.selectedLandingZoneRoleId > 0) {
      const selectedLZR = getSelectedLandingZoneRole(
        localState.selectedLandingZoneRoleId
      );
      form.set(Form.target.VALUE, ["id", selectedLZR.id]);
      form.set(Form.target.VALUE, [
        "landingZoneRoleName",
        selectedLZR.landingZoneRoleName,
      ]);
      form.set(Form.target.VALUE, ["isActive", selectedLZR.isActive]);
    }
  }, [localState.openModal]);

  function getSelectedLandingZoneRole(targetId: number): LandingZoneRoleModel {
    return localState.landingZoneRoles.find(
      (lzr) => lzr.id === targetId
    ) as LandingZoneRoleModel;
  }

  const form = Form.useManager<LandingZoneRoleModel>({
    id: 0,
    landingZoneRoleName: "",
    isActive: false,
  });

  const isShowModal = localState.openModal ? (
    <LandingZoneRoleDetail
      form={form}
      open={localState.openModal}
      onCancel={() => localDispatch({ type: "toggle-modal", value: false })}
    />
  ) : null;

  const pageName = "GCP Landing Zone Roles";
  return (
    <>
      <Box mt={2}>
        <MaintenanceBreadcrumbs title={pageName} />
        <Table
          data={localState.landingZoneRoles.map(
            (lzr: LandingZoneRoleModel) => ({
              cells: [
                {
                  data: lzr.landingZoneRoleName,
                  color: "blue",
                  cursor: "pointer",
                  onClick: () => {
                    localDispatch({
                      type: "toggle-modal",
                      value: true,
                      selectedId: lzr.id,
                    });
                  },
                },
                {
                  data: lzr.isActive ? "Yes" : "No",
                },
              ],
              data: localState.landingZoneRoles,
            })
          )}
          tableName={pageName}
          searchPlaceholder="Search by name"
          searchColumns={[M_TABLE_HEADERS_LANDING_ZONE_ROLES.NAME]}
          headers={headers}
          filters={[FilterType.SEARCH, FilterType.SORT, FilterType.PAGINATOR]}
          toolbar="M_LANDING_ZONE_ROLES_TOOLBAR"
          loading={localState.loading}
        />
        {isShowModal}
      </Box>
    </>
  );
};
